import { HttpRequest, HttpEvent, HttpHandler, HttpErrorResponse, HttpHandlerFn } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { Router } from '@angular/router';


export function authInterceptorFn(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const authService = inject(AuthService);
  let refreshTokenInProgress = false;
  let request$ = new BehaviorSubject<HttpRequest<any>>(null);

  console.log("Request url:", req.url, "timestamp:", (new Date().toISOString()));

  const router = inject(Router);

  // Do not reset timer if we are currently coming from or on the login page or are refreshing the token
  if (router.url !== "/account/login" && !req.url.includes('/connect/token') && authService.currentUser) {
    console.log("Resetting timeout from authInterceptorFn");
    authService.resetTimeout();
  }
  
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {

      // Throw error as usual for non-401 responses.
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Logout if the failed request is the refresh token.
      if (req.url.toLowerCase().includes(authService.loginUrl.toLowerCase())) {
        authService.redirectLogoutUser();
        return throwError('Session expired.');
      }

      if (refreshTokenInProgress) {
        // Wait until the token is refreshed before trying again.
        return request$.pipe(
          filter(r => r !== null),
          take(1),
          switchMap(() => next(addAuthenticationToken(req)))
        );
      }

      refreshTokenInProgress = true;

      // Set to null so subsequest calls will wait for refresh.
      request$.next(null);

      // Store role to reset it after refreshing login.
      const savedRole = authService.currentUser.activeRole;
      const savedProj = this.authService.currentUser.activeProject;
      const savedTeam = this.authService.currentUser.activeTeam;


      // Try again with a refreshed token.
      return authService.refreshLoginOnce().pipe(
        switchMap(_ => {
          refreshTokenInProgress = false;
          authService.setUserActiveRole(savedRole);
          this.authService.setUserActiveProject(savedProj);
          this.authService.setUserActiveTeam(savedTeam);
          this.authService.setUserActiveValsSelected(true);


          // Send the request again with the new token.
          const newRequest = addAuthenticationToken(req);
          request$.next(newRequest);
          return next(newRequest);
        }),
        catchError((refreshError: HttpErrorResponse) => {
          refreshTokenInProgress = false;

          if (refreshError.status === 401) {
            console.log(`REFRESH ERROR 401: REDIRECT LOGOUT`);
            authService.redirectLogoutUser();
            return throwError('Session expired.');
          }

          return throwError(refreshError);
        }));
    }));
}

function addAuthenticationToken(request: HttpRequest<any>) {
  let authServ = inject(AuthService);
  const accessToken = authServ.accessToken;

  // If access token is null this means that user is not logged in and we return the original request.
  if (!accessToken) { return request; }

  return request.clone({
    setHeaders: { Authorization: `Bearer ${accessToken}` }
  });
}
